import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { DEFAULT_CLASS } from "../lib/classification";
import { DEFAULT_RISK } from "../lib/risk";
import ClassSelect from "../components/ClassSelect";
import RiskSelect from "../components/RiskSelect";
import { usePortfolioMetaOptions } from "../hooks/usePortfolioMetaOptions";
import { Link as LinkIcon, ArrowRight, Sparkles, FileUp } from "lucide-react";
import { toast } from "sonner";
import DesignPreviewCard from "../components/DesignPreviewCard";
import DateInput from "../components/DateInput";

export default function NewProject() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const metaOptions = usePortfolioMetaOptions();
  const initialStage = searchParams.get("stage") || null;
  const [stageCatalog, setStageCatalog] = useState([]);
  const initialStageLabel = stageCatalog.find((s) => s.key === initialStage)?.label;
  const [form, setForm] = useState({
    name: "",
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    address: "",
    postcode: "",
    solaredge_site_id: "",
    data_source: "",     // 'solaredge' | 'solis' | 'meteronline' | 'other'
    notes: "",
    assigned_epc: "",
    assigned_investor: "",
    assigned_manager: "",
    business_classification: DEFAULT_CLASS,  // mandatory — driven by CLASSIFICATIONS constant
    risk_status: DEFAULT_RISK,               // RAG rating — defaults to green
    // Portfolio metadata (filterable + rollup on Investor Dashboard)
    unit_code: "",
    property_code: "",
    developer: "",
    operator: "",
    panel_manufacturer: "",
    inverter_manufacturer: "",
    expected_completion_date: "",
    // Commercial model
    is_rentalised: false,
    contracted_monthly_rent_gbp: "",
    fit_export: false,
    fit_provider: "",
    fit_reference: "",
    fit_frequency_months: "",
  });
  const [designerUrl, setDesignerUrl] = useState("");
  const [parsed, setParsed] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [extractedScreenshots, setExtractedScreenshots] = useState([]);
  const [pvsolDiagnostics, setPvsolDiagnostics] = useState(null);
  const [pvsolMatched, setPvsolMatched] = useState(null);   // {matched, total}
  const [variants, setVariants] = useState([]); // [{variant_name, parsed, screenshot_urls, screenshot_count}]
  const [users, setUsers] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [historical, setHistorical] = useState({ enabled: false, completion_date: "", system_size_kwp: "" });
  const [importing, setImporting] = useState(false);
  const [variantsImporting, setVariantsImporting] = useState(false);
  // Mode: "import" (default — via URL/PDF) vs "manual" (data entry)
  const [mode, setMode] = useState("import");
  // Manual design values captured at project creation
  const [manual, setManual] = useState({
    systemSize: "",             // kWp DC
    numberOfModules: "",
    moduleWattage: "",
    inverterModel: "",
    tilt: "",
    azimuth: "",
    annualEnergyProduction: "", // year-1 kWh (degrades 0.5%/yr thereafter)
    specificYield: "",
    performanceRatio: 0.8,
    installation_date: "",      // key input driving degradation
    annual_degradation_pct: 0.5,
  });
  const updManual = (k) => (e) =>
    setManual({ ...manual, [k]: e.target.value });

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get("/users").then(r => setUsers(r.data)).catch(() => {});
    // Load stage catalog so we can display the label of the pre-set stage
    // (from ?stage=…) as a badge in the header.
    if (initialStage) {
      api.get("/pm/roles").then(r => setStageCatalog(r.data.statuses || [])).catch(() => {});
    }
  }, [initialStage]);

  const parseDesigner = async () => {
    setErr("");
    if (!designerUrl) return;
    try {
      const { data } = await api.post("/designer/parse-url", { url: designerUrl });
      setParsed(data);
      if (data.site_id) setForm(f => ({ ...f, solaredge_site_id: data.site_id }));
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
  };

  const aiImport = async () => {
    if (!designerUrl) { toast.error("Paste the proposal URL first"); return; }
    setImporting(true);
    try {
      const start = await api.post("/designs/extract-from-url", { url: designerUrl });
      await pollJob(start.data.job_id, start.data.site_id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setImporting(false);
    }
  };

  const variantsImport = async () => {
    if (!designerUrl) { toast.error("Paste the proposal URL first"); return; }
    setVariantsImporting(true);
    try {
      const start = await api.post("/designs/extract-variants-from-url", { url: designerUrl });
      if (start.data.site_id) setForm(f => ({ ...f, solaredge_site_id: start.data.site_id }));
      toast.info("Detecting variants — this can take up to 3 minutes…");
      const t0 = Date.now();
      while (Date.now() - t0 < 260000) {
        await new Promise(r => setTimeout(r, 3500));
        const j = await api.get(`/jobs/${start.data.job_id}`);
        if (j.data.status === "done") {
          const r = j.data.result;
          const vs = r.variants || [];
          setVariants(vs);
          const withParsed = vs.filter(v => v.parsed);
          if (withParsed.length) {
            // Default the "primary" extracted to the first variant for the create flow
            setExtracted(withParsed[0].parsed);
            setExtractedScreenshots(withParsed[0].screenshot_urls || []);
            toast.success(`Extracted ${withParsed.length} variant${withParsed.length === 1 ? "" : "s"} via Gemini 3.1 Pro`);
          } else {
            toast.error("No variants could be parsed — check the URL is publicly accessible");
          }
          setVariantsImporting(false);
          return;
        }
        if (j.data.status === "error") {
          toast.error(j.data.error || "Variant import failed");
          setVariantsImporting(false);
          return;
        }
      }
      toast.error("Variant import timed out");
      setVariantsImporting(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setVariantsImporting(false);
    }
  };

  const pdfImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const start = await api.post("/designs/extract-from-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.info(`Reading ${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB) — this can take up to 2 minutes…`);
      await pollJob(start.data.job_id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setImporting(false);
    }
  };

  // PV*SOL .pvprj upload — deterministic + synchronous (no vision LLM).
  // Backend returns the extraction inline, so we skip the polling loop and
  // just populate `extracted` / `extractedScreenshots` (empty) directly.
  const pvsolImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/designs/extract-from-pvsol", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.ok && data.parsed) {
        setExtracted(data.parsed);
        setExtractedScreenshots([]);
        setPvsolDiagnostics(data.diagnostics || null);
        setPvsolMatched({ matched: data.matched_count, total: data.total_count });
        toast.success(data.message || "PV*SOL project imported");
      } else {
        setPvsolDiagnostics(null);
        setPvsolMatched(null);
        toast.error(data.message || "PV*SOL parse failed — try manual entry");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  const pollJob = async (jobId, siteId) => {
    if (siteId) setForm(f => ({ ...f, solaredge_site_id: siteId }));
    toast.info("Reading proposal — this can take up to 2 minutes…");
    const t0 = Date.now();
    while (Date.now() - t0 < 220000) {
      await new Promise(r => setTimeout(r, 3000));
      const j = await api.get(`/jobs/${jobId}`);
      if (j.data.status === "done") {
        const r = j.data.result;
        setExtracted(r.parsed);
        setExtractedScreenshots(r.screenshot_urls || []);
        const shotCount = r.screenshot_count || 0;
        const savedCount = (r.screenshot_urls || []).length;
        toast.success(`Read ${shotCount} pages via Gemini${savedCount ? ` · ${savedCount} attached to project` : ""}`);
        setImporting(false);
        return;
      }
      if (j.data.status === "error") {
        toast.error(j.data.error || "Vision import failed");
        setImporting(false);
        return;
      }
    }
    toast.error("Vision import timed out");
    setImporting(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      // Attach commissioning info to the project when manual mode is on
      const projectPayload = mode === "manual" ? {
        ...form,
        installation_date: manual.installation_date || null,
        annual_degradation_pct: manual.annual_degradation_pct === "" || manual.annual_degradation_pct == null
          ? null : Number(manual.annual_degradation_pct),
      } : { ...form };
      if (initialStage) projectPayload.lifecycle_status = initialStage;
      // Historical / already-operational site — drop straight into Final
      // Handover and bucket it on the lifecycle board by its completion year.
      if (historical.enabled) {
        if (!historical.completion_date) {
          setErr("Enter the completion date for the historical site.");
          setBusy(false);
          return;
        }
        projectPayload.lifecycle_status = "final_handover";
        projectPayload.handover_date = historical.completion_date;
        if (historical.system_size_kwp !== "")
          projectPayload.system_size_kwp = Number(historical.system_size_kwp);
        if (!projectPayload.installation_date)
          projectPayload.installation_date = historical.completion_date;
      }
      // Capture the SolarEdge Designer URL on the project itself so it's
      // discoverable even if the Design record is later replaced.
      if (designerUrl && !projectPayload.solaredge_design_url) {
        projectPayload.solaredge_design_url = designerUrl;
      }
      // Commercial-model field coercion (empty string → null/number).
      projectPayload.contracted_monthly_rent_gbp =
        form.contracted_monthly_rent_gbp === "" ? null : Number(form.contracted_monthly_rent_gbp);
      projectPayload.fit_frequency_months =
        form.fit_frequency_months === "" ? null : Number(form.fit_frequency_months);
      if (!form.fit_export) {
        projectPayload.fit_provider = null;
        projectPayload.fit_reference = null;
        projectPayload.fit_frequency_months = null;
      }

      const { data } = await api.post("/projects", projectPayload);

      // Manual design branch — save the entered design fields via /api/designs
      if (mode === "manual") {
        const num = (v) => (v === "" || v == null ? null : Number(v));
        const dp = {
          project_id: data.id,
          source: "manual_entry",
          address: form.address,
          systemSize: num(manual.systemSize),
          numberOfModules: num(manual.numberOfModules),
          moduleWattage: num(manual.moduleWattage),
          inverterModel: manual.inverterModel || null,
          tilt: num(manual.tilt),
          azimuth: num(manual.azimuth),
          annualEnergyProduction: num(manual.annualEnergyProduction),
          specificYield: num(manual.specificYield),
          performanceRatio: num(manual.performanceRatio),
        };
        if (dp.annualEnergyProduction != null) {
          await api.post("/designs", dp);
        }
        nav(`/projects/${data.id}`, { replace: true });
        return;
      }

      // Multi-variant flow: save each detected variant
      const variantsWithParsed = variants.filter(v => v.parsed);
      if (variantsWithParsed.length > 1) {
        // Save the first as primary, the rest as alternative variants
        for (let i = 0; i < variantsWithParsed.length; i++) {
          const v = variantsWithParsed[i];
          await api.post("/designs/add-variant", {
            project_id: data.id,
            variant_name: v.variant_name || `Variant ${i + 1}`,
            make_primary: i === 0,
            solaredge_url: designerUrl || null,
            source: "solaredge_designer_vision",
            address: form.address,
            screenshot_urls: v.screenshot_urls || [],
            ...(v.parsed || {}),
          });
        }
      } else if (designerUrl || extracted) {
        // Preserve `extracted.source` (e.g. "pvsol") when set so the
        // Design tab can render the correct provenance badge; otherwise
        // fall back to SolarEdge-based labelling.
        const source = extracted?.source
          || (extracted ? "solaredge_designer_vision" : "solaredge_designer");
        const payload = {
          project_id: data.id,
          solaredge_url: designerUrl || null,
          source,
          address: form.address,
          screenshot_urls: extractedScreenshots,
          ...(extracted || {}),
        };
        await api.post("/designs", payload);
      }
      nav(`/projects/${data.id}`, { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail, "Failed to create project"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-3xl mx-auto" data-testid="new-project-page">
      <div className="mb-8">
        <div className="label mb-2">Create</div>
        <h1 className="font-display text-4xl font-bold">New Project</h1>
        <p className="muted text-sm mt-2">Capture the site, customer and optionally a SolarEdge Designer share link to pre-populate the design.</p>
        {initialStage && (
          <div
            className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded border border-[var(--accent-hover)]
                       bg-[var(--accent-soft,#DDF3F1)] text-xs"
            data-testid="np-initial-stage-banner"
          >
            <span className="font-mono uppercase tracking-wider text-[10px]" style={{ color: "var(--accent-hover)" }}>
              starts at
            </span>
            <strong style={{ color: "var(--accent-hover)" }}>
              {initialStageLabel || initialStage}
            </strong>
            <span className="muted">— skips the earlier lifecycle stages</span>
          </div>
        )}
      </div>

      <form onSubmit={submit} className="space-y-8" data-testid="new-project-form">
        {/* Historical / already-operational site */}
        <section className="swiss-card-flat p-4" data-testid="np-historical-section">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={historical.enabled} className="mt-1"
                   onChange={(e) => setHistorical({ ...historical, enabled: e.target.checked })}
                   data-testid="np-historical-toggle" />
            <div className="flex-1">
              <div className="label">Historical / already operational site</div>
              <div className="text-xs muted mt-1">
                Adds a site that was completed in a previous year. It drops straight into
                Final Handover and is bucketed on the lifecycle board by its completion year —
                so it never counts in the current pipeline, but still appears in Monitoring &amp; estate reporting.
              </div>
            </div>
          </label>
          {historical.enabled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-[var(--line)]">
              <label className="block">
                <span className="label block mb-1">Completion / handover date *</span>
                <DateInput required className="input-swiss font-mono"
                       max={new Date().toISOString().slice(0, 10)}
                       value={historical.completion_date}
                       onChange={(e) => setHistorical({ ...historical, completion_date: e.target.value })}
                       data-testid="np-historical-completion-date" />
              </label>
              <label className="block">
                <span className="label block mb-1">System size (kWp)</span>
                <input type="number" step="0.01" min="0" className="input-swiss font-mono"
                       placeholder="e.g. 250"
                       value={historical.system_size_kwp}
                       onChange={(e) => setHistorical({ ...historical, system_size_kwp: e.target.value })}
                       data-testid="np-historical-kwp" />
              </label>
            </div>
          )}
        </section>

        {/* Commercial model — rentalised / FIT export */}
        <section className="swiss-card-flat p-4" data-testid="np-commercial-section">
          <div className="label mb-3">Commercial model</div>
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={form.is_rentalised} className="mt-1"
                     onChange={(e) => setForm({ ...form, is_rentalised: e.target.checked })}
                     data-testid="np-rentalised-toggle" />
              <div className="flex-1">
                <div className="font-semibold text-sm">Rentalised site</div>
                <div className="text-xs muted mt-0.5">
                  Investor income is a flat monthly rent (logged in the payments ledger).
                  Generation savings belong to the customer.
                </div>
              </div>
            </label>
            {form.is_rentalised && (
              <label className="block max-w-xs ml-7">
                <span className="label block mb-1">Contracted monthly rent £ (optional)</span>
                <input type="number" step="0.01" min="0" className="input-swiss font-mono"
                       placeholder="e.g. 1500"
                       value={form.contracted_monthly_rent_gbp}
                       onChange={(e) => setForm({ ...form, contracted_monthly_rent_gbp: e.target.value })}
                       data-testid="np-monthly-rent" />
              </label>
            )}
            <label className="flex items-start gap-3 cursor-pointer pt-2 border-t border-[var(--line)]">
              <input type="checkbox" checked={form.fit_export} className="mt-1"
                     onChange={(e) => setForm({ ...form, fit_export: e.target.checked })}
                     data-testid="np-fit-toggle" />
              <div className="flex-1">
                <div className="font-semibold text-sm">FIT export site</div>
                <div className="text-xs muted mt-0.5">
                  Pre-existing Feed-in Tariff scheme — additional income logged every 3/6/12 months.
                </div>
              </div>
            </label>
            {form.fit_export && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 ml-7">
                <label className="block">
                  <span className="label block mb-1">FIT provider</span>
                  <input className="input-swiss" value={form.fit_provider}
                         placeholder="e.g. OVO"
                         onChange={(e) => setForm({ ...form, fit_provider: e.target.value })}
                         data-testid="np-fit-provider" />
                </label>
                <label className="block">
                  <span className="label block mb-1">FIT reference</span>
                  <input className="input-swiss" value={form.fit_reference}
                         placeholder="scheme ref"
                         onChange={(e) => setForm({ ...form, fit_reference: e.target.value })}
                         data-testid="np-fit-reference" />
                </label>
                <label className="block">
                  <span className="label block mb-1">Payment frequency</span>
                  <select className="input-swiss" value={form.fit_frequency_months}
                          onChange={(e) => setForm({ ...form, fit_frequency_months: e.target.value })}
                          data-testid="np-fit-frequency">
                    <option value="">—</option>
                    <option value="3">Every 3 months</option>
                    <option value="6">Every 6 months</option>
                    <option value="12">Every 12 months</option>
                  </select>
                </label>
              </div>
            )}
          </div>
        </section>

        {/* Mode toggle — SolarEdge Import (default) vs Manual entry */}
        <section className="swiss-card-flat p-4" data-testid="np-mode-picker">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="label">Design source</div>
              <div className="text-xs muted mt-1">Choose how you want to bring the PV design into this project.</div>
            </div>
            <div className="flex bg-[var(--surface-2)] border border-[var(--line)]">
              {[
                { k: "import", label: "Import (URL / PDF)" },
                { k: "manual",  label: "Manual entry" },
              ].map((o) => (
                <button key={o.k} type="button" onClick={() => setMode(o.k)}
                        data-testid={`np-mode-${o.k}`}
                        className={`px-4 py-2 text-xs font-mono uppercase tracking-widest ${mode === o.k ? "bg-[var(--ink)] text-white" : "muted"}`}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {mode === "import" && (
        /* SolarEdge Designer Import */
        <section className="swiss-card-flat p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label">Step 1 · SolarEdge Designer import</div>
              <div className="text-xs muted mt-1">Paste a public proposal URL (optional). We parse the site & proposal IDs.</div>
            </div>
            <LinkIcon size={16} className="muted" />
          </div>
          <div className="flex gap-2 flex-wrap">
            <input
              data-testid="designer-url-input"
              className="input-box flex-1 min-w-[240px] font-mono text-xs"
              placeholder="https://designer.solaredge.com/public/en_GB/sites/<id>/proposal/<token>"
              value={designerUrl}
              onChange={(e) => setDesignerUrl(e.target.value)}
            />
            <button type="button" onClick={parseDesigner} className="btn-ghost" data-testid="designer-parse-btn">Parse URL</button>
            <button type="button" onClick={aiImport} disabled={importing || variantsImporting || !designerUrl} className="btn-accent" data-testid="designer-ai-import-btn">
              <Sparkles size={13} /> {importing ? "Reading…" : "Import via URL"}
            </button>
            <button
              type="button"
              onClick={variantsImport}
              disabled={importing || variantsImporting || !designerUrl}
              className="btn-ghost"
              data-testid="designer-variants-import-btn"
              title="Detect and import every design variant (tab) found in the SolarEdge proposal"
            >
              <Sparkles size={13} /> {variantsImporting ? "Detecting variants…" : "Import all variants"}
            </button>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <div className="dotted-divider flex-1" />
            <span className="label">or</span>
            <div className="dotted-divider flex-1" />
          </div>

          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <label className="btn-primary cursor-pointer" data-testid="designer-pdf-import-btn">
              <FileUp size={14} /> {importing ? "Reading PDF…" : "Import from SolarEdge PDF"}
              <input
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                disabled={importing}
                onChange={(e) => pdfImport(e.target.files?.[0])}
                data-testid="designer-pdf-input"
              />
            </label>
            <label className="btn-ghost cursor-pointer" data-testid="pvsol-import-btn"
                   title="Upload a PV*SOL .pvprj project file — kWp, modules, inverter, tilt, azimuth, annual kWh and site coordinates are extracted deterministically (no AI).">
              <FileUp size={14} /> {importing ? "Reading…" : "Import from PV*SOL (.pvprj)"}
              <input
                type="file"
                accept=".pvprj,.zip"
                className="hidden"
                disabled={importing}
                onChange={(e) => pvsolImport(e.target.files?.[0])}
                data-testid="pvsol-file-input"
              />
            </label>
            <div className="text-xs muted flex-1 min-w-[240px]">
              SolarEdge PDF: full AI vision extraction of all pages · PV*SOL .pvprj: instant deterministic parse of design fields.
            </div>
          </div>
          {parsed && (
            <div className="mt-3 text-xs font-mono muted" data-testid="designer-parsed-result">
              site_id: <span className="text-[var(--ink)]">{parsed.site_id || "—"}</span> · proposal_id: <span className="text-[var(--ink)]">{parsed.proposal_id || "—"}</span>
            </div>
          )}
          {extracted && (
            <div className="mt-4 p-4 bg-[var(--surface)] border-l-2 border-[var(--signal)]" data-testid="designer-extracted-result">
              <div className="label mb-2">
                {extracted.source === "pvsol"
                  ? "Extracted from PV*SOL project"
                  : "Extracted via Gemini 3.1 Pro vision"}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono">
                <div><div className="muted">System size</div><div>{extracted.systemSize ?? "—"} kWp</div></div>
                <div><div className="muted">Modules</div><div>{extracted.numberOfModules ?? "—"}</div></div>
                <div><div className="muted">Annual kWh</div><div>{extracted.annualEnergyProduction ?? "—"}</div></div>
                <div><div className="muted">Specific yield</div><div>{extracted.specificYield ?? "—"} kWh/kWp</div></div>
                <div><div className="muted">Module</div><div className="truncate">{extracted.moduleModel || "—"}</div></div>
                <div><div className="muted">Inverter</div><div className="truncate">{extracted.inverterModel || "—"}</div></div>
                <div><div className="muted">Tilt / Azim</div><div>{extracted.tilt ?? "—"}° / {extracted.azimuth ?? "—"}°</div></div>
                <div><div className="muted">PR</div><div>{extracted.performanceRatio ?? "—"}</div></div>
              </div>
              <div className="text-xs muted mt-3">
                These values will populate the project&apos;s design tab on create.
                {extractedScreenshots.length > 0 && ` · ${extractedScreenshots.length} screenshot${extractedScreenshots.length === 1 ? "" : "s"} will be attached to Documents.`}
              </div>

              {/* PV*SOL diagnostics — matched vs missing field breakdown */}
              {pvsolDiagnostics && pvsolMatched && (
                <details className="mt-3 pt-3 border-t border-[var(--line-soft)]" data-testid="pvsol-diagnostics">
                  <summary className="cursor-pointer text-[11px] font-mono uppercase tracking-widest muted hover:text-[var(--ink)]">
                    Which .pvprj tags matched? · <span className="text-[var(--ink)] font-semibold">{pvsolMatched.matched}</span> of {pvsolMatched.total} captured
                  </summary>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                    {pvsolDiagnostics.map((d) => (
                      <div key={d.field}
                           className={`flex items-baseline gap-2 py-0.5 ${d.matched ? "" : "opacity-60"}`}
                           data-testid={`pvsol-diag-${d.field}`}>
                        <span className={`inline-block w-3 shrink-0 ${d.matched ? "text-emerald-600" : "text-amber-600"}`}>
                          {d.matched ? "✓" : "—"}
                        </span>
                        <span className="font-medium">{d.label}</span>
                        {d.derived && <span className="tag signal text-[9px]">derived</span>}
                        <span className="muted font-mono text-[10px] truncate">{d.tag_hint}</span>
                      </div>
                    ))}
                  </div>
                  {pvsolMatched.matched < pvsolMatched.total && (
                    <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 p-2">
                      {pvsolMatched.total - pvsolMatched.matched} field(s) weren&apos;t found in this .pvprj file — you&apos;ll need to fill them in manually on the project&apos;s Design tab.
                    </div>
                  )}
                </details>
              )}
            </div>
          )}

          {variants.length > 0 && (
            <div className="mt-4 p-4 bg-[var(--surface-2)] border-l-2 border-[var(--accent)]" data-testid="designer-variants-result">
              <div className="label mb-3">
                {variants.length} variant{variants.length === 1 ? "" : "s"} detected — all will be saved to the project
              </div>
              <div className="space-y-2">
                {variants.map((v, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between text-xs border-b border-[var(--line-soft)] pb-1.5 last:border-0"
                    data-testid={`variant-row-${i}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-mono uppercase tracking-wider text-[10px] muted">#{i + 1}</span>
                      <span className="font-medium">{v.variant_name}</span>
                      {i === 0 && <span className="tag signal">primary</span>}
                    </div>
                    <div className="font-mono muted text-[11px]">
                      {v.parsed?.systemSize ? `${v.parsed.systemSize} kWp · ${v.parsed.annualEnergyProduction ?? "—"} kWh/yr` : "Parse failed"}
                      {" · "}
                      {v.screenshot_count} page{v.screenshot_count === 1 ? "" : "s"}
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-[11px] muted mt-3">
                The first variant becomes the project&apos;s primary design. You can switch which variant
                is primary later from the project&apos;s Design tab.
              </div>
            </div>
          )}
        </section>
        )}

        {mode === "manual" && (
          <section className="swiss-card-flat p-6" data-testid="np-manual-section">
            <div className="mb-4">
              <div className="label">Step 1 · Manual design entry</div>
              <div className="text-xs muted mt-1">
                Enter <strong>Year-1 simulated production</strong> and the installation date.
                Subsequent years will auto-degrade at <span className="font-mono">{manual.annual_degradation_pct}%/yr</span> from the installation anniversary.
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="label block mb-1">System size (kWp DC) *</label>
                <input type="number" step="0.01" className="input-swiss font-mono"
                       value={manual.systemSize} onChange={updManual("systemSize")}
                       data-testid="np-manual-kwp" required placeholder="222.36" />
              </div>
              <div>
                <label className="label block mb-1">Number of modules</label>
                <input type="number" className="input-swiss font-mono"
                       value={manual.numberOfModules} onChange={updManual("numberOfModules")}
                       data-testid="np-manual-modules" placeholder="436" />
              </div>
              <div>
                <label className="label block mb-1">Module wattage (W)</label>
                <input type="number" className="input-swiss font-mono"
                       value={manual.moduleWattage} onChange={updManual("moduleWattage")}
                       data-testid="np-manual-wattage" placeholder="510" />
              </div>
              <div className="md:col-span-2">
                <label className="label block mb-1">Inverter model</label>
                <input className="input-swiss" value={manual.inverterModel} onChange={updManual("inverterModel")}
                       data-testid="np-manual-inverter" placeholder="SolarEdge SE100K Synergy Manager" />
              </div>
              <div>
                <label className="label block mb-1">Performance ratio</label>
                <input type="number" step="0.01" min="0.5" max="1" className="input-swiss font-mono"
                       value={manual.performanceRatio} onChange={updManual("performanceRatio")}
                       data-testid="np-manual-pr" />
              </div>
              <div>
                <label className="label block mb-1">Tilt (°)</label>
                <input type="number" step="0.1" className="input-swiss font-mono"
                       value={manual.tilt} onChange={updManual("tilt")}
                       data-testid="np-manual-tilt" placeholder="15" />
              </div>
              <div>
                <label className="label block mb-1">Azimuth (°)</label>
                <input type="number" step="0.1" className="input-swiss font-mono"
                       value={manual.azimuth} onChange={updManual("azimuth")}
                       data-testid="np-manual-azimuth" placeholder="180" />
              </div>
              <div>
                <label className="label block mb-1">Specific yield (kWh/kWp)</label>
                <input type="number" step="0.1" className="input-swiss font-mono"
                       value={manual.specificYield} onChange={updManual("specificYield")}
                       data-testid="np-manual-yield" placeholder="966" />
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-[var(--line)] grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="label block mb-1">Year-1 simulated (kWh/yr) *</label>
                <input type="number" step="1" className="input-swiss font-mono"
                       value={manual.annualEnergyProduction} onChange={updManual("annualEnergyProduction")}
                       data-testid="np-manual-year1" required placeholder="214750" />
                <div className="text-[11px] muted mt-1">Baseline before degradation.</div>
              </div>
              <div>
                <label className="label block mb-1">Installation date *</label>
                <DateInput className="input-swiss font-mono"
                       value={manual.installation_date} onChange={updManual("installation_date")}
                       data-testid="np-manual-install-date" required />
                <div className="text-[11px] muted mt-1">Drives Year-N calculation.</div>
              </div>
              <div>
                <label className="label block mb-1">Annual degradation (%/yr)</label>
                <input type="number" step="0.01" min="0" max="5" className="input-swiss font-mono"
                       value={manual.annual_degradation_pct} onChange={updManual("annual_degradation_pct")}
                       data-testid="np-manual-degradation" />
                <div className="text-[11px] muted mt-1">PV industry norm: 0.5%.</div>
              </div>
            </div>

            {manual.annualEnergyProduction && manual.installation_date && (
              <div className="mt-5 bg-[var(--surface-2)] p-4 border border-[var(--line)]"
                   data-testid="np-manual-degradation-preview">
                <div className="label mb-2">Projected annual production (post-degradation)</div>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-xs font-mono">
                  {[1, 5, 10, 15, 20, 25].map((yr) => {
                    const deg = Number(manual.annual_degradation_pct) || 0.5;
                    const factor = yr === 1 ? 1 : (1 - deg / 100) ** (yr - 1);
                    const kwh = Number(manual.annualEnergyProduction) * factor;
                    return (
                      <div key={yr} className="p-2 bg-white border border-[var(--line)]">
                        <div className="muted text-[10px] uppercase">Year {yr}</div>
                        <div className="text-[var(--ink)] font-bold">
                          {kwh > 999 ? `${(kwh / 1000).toFixed(1)} MWh` : `${kwh.toFixed(0)} kWh`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Project Details */}
        <section className="swiss-card-flat p-6">
          <div className="label mb-4">Step 2 · Project details</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <label className="label block mb-1">Project name *</label>
              <input data-testid="np-name" required className="input-swiss" value={form.name} onChange={upd("name")} placeholder="e.g. Mill Lane Warehouse PV" />
            </div>
            <div>
              <label className="label block mb-1">Customer name *</label>
              <input data-testid="np-customer-name" required className="input-swiss" value={form.customer_name} onChange={upd("customer_name")} />
            </div>
            <div>
              <label className="label block mb-1">Business classification <span className="text-[var(--danger,#D64545)]">*</span></label>
              <ClassSelect
                testId="np-classification"
                value={form.business_classification}
                onChange={(v) => setForm((f) => ({ ...f, business_classification: v }))}
              />
            </div>
            <div>
              <label className="label block mb-1">Risk rating (RAG)</label>
              <RiskSelect
                testId="np-risk"
                value={form.risk_status}
                onChange={(v) => setForm((f) => ({ ...f, risk_status: v }))}
              />
              <div className="text-[10px] muted mt-1">
                Defaults to Green. Flag to Amber or Red so the Investor Dashboard surfaces sites needing attention.
              </div>
            </div>
            <div>
              <label className="label block mb-1">Unit code</label>
              <input data-testid="np-unit-code" className="input-swiss" value={form.unit_code} onChange={upd("unit_code")} placeholder="e.g. U-24" />
            </div>
            <div>
              <label className="label block mb-1">Property code</label>
              <input data-testid="np-property-code" className="input-swiss" value={form.property_code} onChange={upd("property_code")} placeholder="e.g. PC-01" />
            </div>
            <div>
              <label className="label block mb-1">Developer</label>
              <input data-testid="np-developer" className="input-swiss" list="np-developer-options" value={form.developer} onChange={upd("developer")} placeholder="e.g. Indurent" />
              <datalist id="np-developer-options">
                {metaOptions.developer.map(v => <option key={v} value={v} />)}
              </datalist>
            </div>
            <div>
              <label className="label block mb-1">Operator</label>
              <input data-testid="np-operator" className="input-swiss" list="np-operator-options" value={form.operator} onChange={upd("operator")} placeholder="e.g. Flow Energy" />
              <datalist id="np-operator-options">
                {metaOptions.operator.map(v => <option key={v} value={v} />)}
              </datalist>
            </div>
            <div>
              <label className="label block mb-1">Panel manufacturer</label>
              <input data-testid="np-panel-mfr" className="input-swiss" list="np-panel-options" value={form.panel_manufacturer} onChange={upd("panel_manufacturer")} placeholder="e.g. JinkoSolar" />
              <datalist id="np-panel-options">
                {metaOptions.panel_manufacturer.map(v => <option key={v} value={v} />)}
              </datalist>
            </div>
            <div>
              <label className="label block mb-1">Inverter manufacturer</label>
              <input data-testid="np-inverter-mfr" className="input-swiss" list="np-inverter-options" value={form.inverter_manufacturer} onChange={upd("inverter_manufacturer")} placeholder="e.g. SolarEdge" />
              <datalist id="np-inverter-options">
                {metaOptions.inverter_manufacturer.map(v => <option key={v} value={v} />)}
              </datalist>
            </div>
            <div>
              <label className="label block mb-1">Customer email</label>
              <input data-testid="np-customer-email" type="email" className="input-swiss" value={form.customer_email} onChange={upd("customer_email")} />
            </div>
            <div>
              <label className="label block mb-1">Customer phone</label>
              <input data-testid="np-customer-phone" className="input-swiss" value={form.customer_phone} onChange={upd("customer_phone")} />
            </div>
            <div>
              <label className="label block mb-1">Expected completion date</label>
              <DateInput data-testid="np-expected-completion" className="input-swiss font-mono"
                     value={form.expected_completion_date}
                     onChange={upd("expected_completion_date")} />
              <div className="text-[10px] muted mt-1">
                Planned handover date. The very first value you save is snapshotted as the baseline so slippage can be tracked later.
              </div>
            </div>
            <div>
              <label className="label block mb-1">Solar system type</label>
              <select data-testid="np-system-type"
                      className="input-swiss"
                      value={form.data_source}
                      onChange={upd("data_source")}>
                <option value="">— pick a monitoring source —</option>
                <option value="solaredge">SolarEdge</option>
                <option value="solis">Solis (Ginlong SolisCloud)</option>
                <option value="meteronline">MeterOnline (HH meter)</option>
                <option value="other">Other / manual / no monitoring yet</option>
              </select>
              <div className="text-[10px] muted mt-1">
                Determines which API this site syncs generation data from. Credentials configured on the project&apos;s Analytics tab after create.
              </div>
            </div>
            {form.data_source === "solaredge" && (
              <div>
                <label className="label block mb-1">SolarEdge Site ID</label>
                <input data-testid="np-site-id" className="input-swiss font-mono text-xs"
                       value={form.solaredge_site_id}
                       onChange={upd("solaredge_site_id")}
                       placeholder="auto-detected from URL above" />
              </div>
            )}
            <div className="md:col-span-2">
              <label className="label block mb-1">Site address *</label>
              <input data-testid="np-address" required className="input-swiss" value={form.address} onChange={upd("address")} placeholder="Street, City" />
            </div>
            <div>
              <label className="label block mb-1">Postcode</label>
              <input data-testid="np-postcode" className="input-swiss" value={form.postcode} onChange={upd("postcode")} placeholder="SW1A 1AA" />
            </div>
            <div className="md:col-span-2">
              <label className="label block mb-1">Notes</label>
              <textarea data-testid="np-notes" className="input-box" rows={3} value={form.notes} onChange={upd("notes")} />
            </div>
          </div>
        </section>

        <section className="swiss-card-flat p-6">
          <div className="label mb-4">Step 3 · Assignments (optional)</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <UserPicker label="EPC / Installer" testid="np-epc" users={users} role="epc" value={form.assigned_epc} onChange={upd("assigned_epc")} />
            <UserPicker label="Investor" testid="np-investor" users={users} role="investor" value={form.assigned_investor} onChange={upd("assigned_investor")} />
            <UserPicker label="Project Manager" testid="np-manager" users={users} role="manager" value={form.assigned_manager} onChange={upd("assigned_manager")} />
          </div>
        </section>

        {err && <div className="text-xs text-[var(--err)] font-mono" data-testid="np-error">{err}</div>}

        <DesignPreviewCard
          data={
            extracted
              ? extracted
              : (mode === "manual" ? {
                  systemSize: manual.systemSize,
                  numberOfModules: manual.numberOfModules,
                  moduleWattage: manual.moduleWattage,
                  moduleModel: manual.moduleModel,
                  inverterModel: manual.inverterModel,
                  inverterCount: manual.inverterCount,
                  annualEnergyProduction: manual.annualEnergyProduction,
                  source: "manual entry",
                } : null)
          }
          sourceHint={designerUrl}
          testid="np-import-preview"
        />

        <div className="flex justify-end gap-3">
          <button type="button" className="btn-ghost" onClick={() => nav(-1)} data-testid="np-cancel-btn">Cancel</button>
          <button type="submit" disabled={busy} className="btn-accent" data-testid="np-submit-btn">
            {busy ? "Creating…" : <>Create project <ArrowRight size={14} /></>}
          </button>
        </div>
      </form>
    </div>
  );
}

function UserPicker({ label, users, role, value, onChange, testid }) {
  const filtered = users.filter(u => !role || u.role === role || u.role === "admin");
  return (
    <label className="block" data-testid={testid}>
      <span className="label block mb-1">{label}</span>
      <select className="input-box" value={value || ""} onChange={onChange}>
        <option value="">— unassigned —</option>
        {filtered.map(u => (
          <option key={u.id} value={u.id}>{`${u.name} · ${u.email}`}</option>
        ))}
      </select>
    </label>
  );
}
